import type { FinishTaskType, FinishTabletIssuance, FinishTabletTask, FinishTabletWorker, PublicWorker } from "./types.js";

export const finishTaskType = (name: string): FinishTaskType => {
  const compact = name.replace(/\s/g, "");
  if (compact.includes("뒤집")) return "turning";
  if (compact.includes("솜") && /충진|충전|넣/.test(compact)) return "stuffing";
  if (compact.includes("공그르")) return "ladder_stitch";
  return "other";
};

export const tabletWorkerState = (workerId: string, activeIssuances: FinishTabletIssuance[], stage: "sewing" | "finish") =>
  activeIssuances.some((issuance) => issuance.workerId === workerId && issuance.stage === stage) ? "working" as const : "idle" as const;

export const finishWorkerState = (workerId: string, activeIssuances: FinishTabletIssuance[]) =>
  tabletWorkerState(workerId, activeIssuances, "finish");

export const finishWorkersByState = <T extends PublicWorker>(
  workers: T[],
  activeIssuances: FinishTabletIssuance[],
  state: "working" | "idle",
) => workers.filter((worker) => finishWorkerState(worker.id, activeIssuances) === state);

export const tabletWorkersByState = <T extends PublicWorker>(
  workers: T[],
  activeIssuances: FinishTabletIssuance[],
  stage: "sewing" | "finish",
  state: "working" | "idle",
) => workers.filter((worker) => tabletWorkerState(worker.id, activeIssuances, stage) === state);

const koreanInitials = ["ㄱ", "ㄱ", "ㄴ", "ㄷ", "ㄷ", "ㄹ", "ㅁ", "ㅂ", "ㅂ", "ㅅ", "ㅅ", "ㅇ", "ㅈ", "ㅈ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ"] as const;

export const koreanInitialsOf = (value: string) => [...value.normalize("NFC")].map((character) => {
  const code = character.charCodeAt(0);
  return code >= 0xac00 && code <= 0xd7a3 ? koreanInitials[Math.floor((code - 0xac00) / 588)] : character;
}).join("").replace(/\s/g, "");

export const matchesKoreanInitials = (name: string, query: string) =>
  !query || koreanInitialsOf(name).startsWith(query);

const koreanInitialCharacters = new Set(["ㄱ", "ㄴ", "ㄷ", "ㄹ", "ㅁ", "ㅂ", "ㅅ", "ㅇ", "ㅈ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ"]);

/** 일반 문자열 포함 검색과 `강ㅁ` 같은 한글/초성 혼합 검색을 함께 지원합니다. */
export const matchesKoreanName = (name: string, query: string) => {
  const normalizedName = name.normalize("NFC").toLocaleLowerCase("ko-KR");
  const normalizedQuery = query.trim().normalize("NFC").toLocaleLowerCase("ko-KR");
  if (!normalizedQuery || normalizedName.includes(normalizedQuery)) return true;
  const nameCharacters = [...normalizedName];
  const queryCharacters = [...normalizedQuery];
  if (queryCharacters.length > nameCharacters.length) return false;
  return nameCharacters.some((_, start) => queryCharacters.every((character, offset) => {
    const candidate = nameCharacters[start + offset];
    if (candidate === undefined) return false;
    return koreanInitialCharacters.has(character)
      ? koreanInitialsOf(candidate) === character
      : candidate === character;
  }));
};

export const finishWorkerCandidates = (
  workers: FinishTabletWorker[],
  activeIssuances: FinishTabletIssuance[],
  state: "working" | "idle",
  query: string,
) => {
  const normalizedQuery = query.trim();
  return finishWorkersByState(workers, activeIssuances, state).filter(worker => {
    if (state === "working") return matchesKoreanInitials(worker.displayedName, normalizedQuery);
    if (!normalizedQuery) return worker.hasFinishGroup;
    return matchesKoreanInitials(worker.displayedName, normalizedQuery);
  });
};

export const tabletWorkerCandidates = (
  workers: FinishTabletWorker[],
  activeIssuances: FinishTabletIssuance[],
  stage: "sewing" | "finish",
  state: "working" | "idle" | "all",
  query: string,
) => {
  const normalizedQuery = query.trim();
  const candidates = state === "all" ? workers : tabletWorkersByState(workers, activeIssuances, stage, state);
  return candidates.filter(worker => {
    if (state === "working") return matchesKoreanInitials(worker.displayedName, normalizedQuery);
    if (!normalizedQuery) return worker.hasStageGroup ?? worker.hasFinishGroup;
    // 초성 검색 중에는 기존 배정 타입과 관계없이 일치하는 모든 작업자를 보여준다.
    return matchesKoreanInitials(worker.displayedName, normalizedQuery);
  });
};

export const groupFinishTasks = (tasks: FinishTabletTask[]) => {
  const groups = new Map<string, { projectId: string; projectName: string; tasks: FinishTabletTask[] }>();
  for (const task of tasks) {
    const group: { projectId: string; projectName: string; tasks: FinishTabletTask[] } = groups.get(task.projectId)
      ?? { projectId: task.projectId, projectName: task.projectName, tasks: [] };
    group.tasks.push(task);
    groups.set(task.projectId, group);
  }
  return [...groups.values()];
};

export const normalizeRfidTag = (value: string) => value
  .replaceAll("\x02", "")
  .replaceAll("\x03", "")
  .trim()
  .toUpperCase();
